import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, CheckCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
interface ReceiptSummaryProps {
  totalThisReceipt: number;
  totalReceived: number;
  totalRemaining: number;
  poTotal: number;
  currency: string;
  locale?: string;
}
export function ReceiptSummary({
  totalThisReceipt,
  totalReceived,
  totalRemaining,
  poTotal,
  currency,
  locale = 'en-US'
}: ReceiptSummaryProps) {
  // Calculate percentages for progress bars
  const receivedPercentage = totalReceived / poTotal * 100;
  const thisReceiptPercentage = totalThisReceipt / poTotal * 100;
  const remainingPercentage = totalRemaining / poTotal * 100;
  // Calculate combined percentage (received + this receipt)
  const combinedPercentage = receivedPercentage + thisReceiptPercentage;
  // Check if we're at 100% after this receipt
  const isComplete = Math.abs(combinedPercentage - 100) < 0.01;
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-14825">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-14826">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14827">
          Receipt Summary
        </h3>
      </div>
      <div className="px-4 py-5 sm:p-6" data-id="element-14828">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-14829">
          <div className="p-4 bg-green-50 rounded-lg" data-id="element-14830">
            <div className="flex justify-between items-center" data-id="element-14831">
              <h4 className="text-sm font-medium text-green-800" data-id="element-14832">
                Total This Receipt
              </h4>
              <ArrowUpIcon className="h-4 w-4 text-green-600" data-id="element-14833" />
            </div>
            <p className="text-2xl font-mono font-semibold text-green-700" data-id="element-14834">
              {formatCurrency(totalThisReceipt, currency, locale)}
            </p>
            <div className="mt-2" data-id="element-14835">
              <div className="text-xs text-green-700 mb-1" data-id="element-14836">
                {thisReceiptPercentage.toFixed(1)}% of PO Total
              </div>
              <div className="w-full bg-green-200 rounded-full h-2" data-id="element-14837">
                <div className="bg-green-600 h-2 rounded-full" style={{
                width: `${thisReceiptPercentage}%`
              }} data-id="element-14838"></div>
              </div>
            </div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg" data-id="element-14839">
            <div className="flex justify-between items-center" data-id="element-14840">
              <h4 className="text-sm font-medium text-blue-800" data-id="element-14841">
                Total Received To Date
              </h4>
              <CheckCircleIcon className="h-4 w-4 text-blue-600" data-id="element-14842" />
            </div>
            <p className="text-2xl font-mono font-semibold text-blue-700" data-id="element-14843">
              {formatCurrency(totalReceived, currency, locale)}
            </p>
            <div className="mt-2" data-id="element-14844">
              <div className="text-xs text-blue-700 mb-1" data-id="element-14845">
                {receivedPercentage.toFixed(1)}% of PO Total
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2" data-id="element-14846">
                <div className="bg-blue-600 h-2 rounded-full" style={{
                width: `${receivedPercentage}%`
              }} data-id="element-14847"></div>
              </div>
            </div>
          </div>
          <div className="p-4 bg-amber-50 rounded-lg" data-id="element-14848">
            <div className="flex justify-between items-center" data-id="element-14849">
              <h4 className="text-sm font-medium text-amber-800" data-id="element-14850">
                Remaining After Receipt
              </h4>
              {isComplete ? <CheckCircleIcon className="h-4 w-4 text-green-600" data-id="element-14851" /> : <AlertTriangleIcon className="h-4 w-4 text-amber-600" data-id="element-14852" />}
            </div>
            <p className="text-2xl font-mono font-semibold text-amber-700" data-id="element-14853">
              {formatCurrency(totalRemaining - totalThisReceipt, currency, locale)}
            </p>
            <div className="mt-2" data-id="element-14854">
              <div className="text-xs text-amber-700 mb-1" data-id="element-14855">
                {(remainingPercentage - thisReceiptPercentage).toFixed(1)}% of
                PO Total
              </div>
              <div className="w-full bg-amber-200 rounded-full h-2" data-id="element-14856">
                <div className="bg-amber-600 h-2 rounded-full" style={{
                width: `${remainingPercentage - thisReceiptPercentage}%`
              }} data-id="element-14857"></div>
              </div>
            </div>
          </div>
        </div>
        {/* Combined progress bar */}
        <div className="mt-6" data-id="element-14858">
          <div className="flex justify-between items-center mb-2" data-id="element-14859">
            <h4 className="text-sm font-medium text-gray-700" data-id="element-14860">
              Overall PO Receipt Progress
            </h4>
            <div className="text-sm text-gray-500" data-id="element-14861">
              PO Total: {formatCurrency(poTotal, currency, locale)}
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden" data-id="element-14862">
            <div className="flex h-full" data-id="element-14863">
              <div className="bg-blue-600 h-4" style={{
              width: `${receivedPercentage}%`
            }} data-id="element-14864"></div>
              <div className="bg-green-500 h-4" style={{
              width: `${thisReceiptPercentage}%`
            }} data-id="element-14865"></div>
            </div>
          </div>
          <div className="flex mt-2 text-xs" data-id="element-14866">
            <div className="flex items-center mr-4" data-id="element-14867">
              <div className="w-3 h-3 bg-blue-600 rounded-full mr-1" data-id="element-14868"></div>
              <span data-id="element-14869">Already Received ({receivedPercentage.toFixed(1)}%)</span>
            </div>
            <div className="flex items-center mr-4" data-id="element-14870">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-1" data-id="element-14871"></div>
              <span data-id="element-14872">This Receipt ({thisReceiptPercentage.toFixed(1)}%)</span>
            </div>
            <div className="flex items-center" data-id="element-14873">
              <div className="w-3 h-3 bg-gray-300 rounded-full mr-1" data-id="element-14874"></div>
              <span data-id="element-14875">Remaining ({(100 - combinedPercentage).toFixed(1)}%)</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}